/**
 * Deterministic scoring of a page's *metadata*.
 *
 * Scope is deliberate: these are the checks that can be answered from the fields in
 * front of the editor, with no server round trip, so the score updates as they type.
 * Content-quality scoring — search-intent match, entity coverage, LLM-extractability —
 * needs the rendered page and an LLM, so it belongs to the phase-4 server-side scorer.
 * Keep this file honest about that boundary rather than guessing at content quality
 * from a meta description.
 *
 * Lengths are the points at which Google truncates, and match the constraints on
 * `seo.SeoMeta` (`titles` max 60, `descriptions` max 160).
 */

export const TITLE_MIN_LENGTH = 25;
export const TITLE_MAX_LENGTH = 60;
export const DESCRIPTION_MIN_LENGTH = 70;
export const DESCRIPTION_MAX_LENGTH = 160;

export type CheckStatus = 'pass' | 'warn' | 'fail';

export interface SeoCheck {
  key: string;
  label: string;
  status: CheckStatus;
  detail: string;
  /** Weight in the overall score. A missing title costs far more than a missing image. */
  weight: number;
}

export interface SeoMetaValues {
  title?: string | null;
  description?: string | null;
  ogImage?: string | null;
  focusKeyword?: string | null;
  noindex?: boolean | null;
}

const contains = (haystack: string | null | undefined, needle: string) =>
  !!haystack && haystack.toLowerCase().includes(needle.toLowerCase());

const checkTitle = (title?: string | null): SeoCheck => {
  const length = (title || '').trim().length;
  if (!length) {
    return {
      key: 'title',
      label: 'Title',
      status: 'fail',
      detail: 'Missing — the page falls back to a generated title.',
      weight: 30,
    };
  }
  if (length > TITLE_MAX_LENGTH) {
    return {
      key: 'title',
      label: 'Title',
      status: 'warn',
      detail: `${length} chars — Google truncates past ${TITLE_MAX_LENGTH}.`,
      weight: 30,
    };
  }
  if (length < TITLE_MIN_LENGTH) {
    return {
      key: 'title',
      label: 'Title',
      status: 'warn',
      detail: `${length} chars — short enough to be wasting space.`,
      weight: 30,
    };
  }
  return { key: 'title', label: 'Title', status: 'pass', detail: `${length} chars.`, weight: 30 };
};

const checkDescription = (description?: string | null): SeoCheck => {
  const length = (description || '').trim().length;
  if (!length) {
    return {
      key: 'description',
      label: 'Description',
      status: 'fail',
      detail: 'Missing — Google will invent a snippet from the page body.',
      weight: 25,
    };
  }
  if (length > DESCRIPTION_MAX_LENGTH) {
    return {
      key: 'description',
      label: 'Description',
      status: 'warn',
      detail: `${length} chars — truncated past ${DESCRIPTION_MAX_LENGTH}.`,
      weight: 25,
    };
  }
  if (length < DESCRIPTION_MIN_LENGTH) {
    return {
      key: 'description',
      label: 'Description',
      status: 'warn',
      detail: `${length} chars — room for more detail.`,
      weight: 25,
    };
  }
  return {
    key: 'description',
    label: 'Description',
    status: 'pass',
    detail: `${length} chars.`,
    weight: 25,
  };
};

const checkFocusKeyword = (values: SeoMetaValues): SeoCheck => {
  const keyword = (values.focusKeyword || '').trim();
  if (!keyword) {
    return {
      key: 'focus_keyword',
      label: 'Focus keyword',
      status: 'warn',
      detail: 'Not set — set one to check placement and to track it in the dashboard.',
      weight: 20,
    };
  }

  const inTitle = contains(values.title, keyword);
  const inDescription = contains(values.description, keyword);

  if (inTitle && inDescription) {
    return {
      key: 'focus_keyword',
      label: 'Focus keyword',
      status: 'pass',
      detail: `"${keyword}" appears in both the title and the description.`,
      weight: 20,
    };
  }
  if (inTitle || inDescription) {
    return {
      key: 'focus_keyword',
      label: 'Focus keyword',
      status: 'warn',
      detail: `"${keyword}" is missing from the ${inTitle ? 'description' : 'title'}.`,
      weight: 20,
    };
  }
  return {
    key: 'focus_keyword',
    label: 'Focus keyword',
    status: 'fail',
    detail: `"${keyword}" appears in neither the title nor the description.`,
    weight: 20,
  };
};

const checkOgImage = (ogImage?: string | null): SeoCheck =>
  ogImage
    ? {
        key: 'og_image',
        label: 'Share image',
        status: 'pass',
        detail: 'Set.',
        weight: 15,
      }
    : {
        key: 'og_image',
        label: 'Share image',
        status: 'warn',
        detail: 'Not set — the page falls back to its own cover image.',
        weight: 15,
      };

const checkIndexable = (noindex?: boolean | null): SeoCheck =>
  noindex
    ? {
        key: 'noindex',
        label: 'Indexing',
        status: 'fail',
        detail: 'This page is excluded from search engines.',
        weight: 10,
      }
    : {
        key: 'noindex',
        label: 'Indexing',
        status: 'pass',
        detail: 'Indexable.',
        weight: 10,
      };

export interface SeoScore {
  /** 0–100. */
  score: number;
  checks: SeoCheck[];
  status: CheckStatus;
}

const STATUS_CREDIT: Record<CheckStatus, number> = { pass: 1, warn: 0.5, fail: 0 };

export const scoreSeoMeta = (values: SeoMetaValues): SeoScore => {
  const checks = [
    checkTitle(values.title),
    checkDescription(values.description),
    checkFocusKeyword(values),
    checkOgImage(values.ogImage),
    checkIndexable(values.noindex),
  ];

  const totalWeight = checks.reduce((sum, check) => sum + check.weight, 0);
  const earned = checks.reduce(
    (sum, check) => sum + check.weight * STATUS_CREDIT[check.status],
    0,
  );
  const score = Math.round((earned / totalWeight) * 100);

  // A deliberate noindex isn't a failing page, so it doesn't drag the headline status
  // to "fail" on its own — but a missing title does.
  const hasBlockingFailure = checks.some(
    check => check.status === 'fail' && check.key !== 'noindex',
  );

  let status: CheckStatus = 'pass';
  if (hasBlockingFailure || score < 50) status = 'fail';
  else if (score < 85) status = 'warn';

  return { score, checks, status };
};

/** antd Badge/Progress colour for a status. */
export const STATUS_COLOR: Record<CheckStatus, string> = {
  pass: '#26b326',
  warn: '#faad14',
  fail: '#f85959',
};
